import { Component, EventEmitter, Input, OnInit, Output, ElementRef, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { PartnerService, CreateLeadRequest } from '../../../../../core/services/partner.service';
import { finalize } from 'rxjs/operators';

@Component({
    selector: 'app-registration-modal',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule],
    templateUrl: './registration-modal.html',
    styleUrl: './registration-modal.scss'
})
export class RegistrationModalComponent implements OnInit, OnDestroy {
    @Input() mobileNumber: string = '';
    @Output() close = new EventEmitter<void>();
    @Output() success = new EventEmitter<string>(); // emmits leadId

    registrationForm!: FormGroup;
    currentStep: number = 1;
    totalSteps: number = 3;
    isLoading: boolean = false;
    isSuccess: boolean = false;
    errorMessage: string | null = null;
    receivedLeadId: string | null = null;

    constructor(
        private fb: FormBuilder,
        private partnerService: PartnerService,
        private el: ElementRef,
        private cdr: ChangeDetectorRef
    ) {
        this.initForm();
    }

    ngOnInit(): void {
        // Move to body to escape widget container's 'overflow: hidden' and masonry 'transform'
        document.body.appendChild(this.el.nativeElement);
    }

    ngOnDestroy(): void {
        if (this.el.nativeElement.parentNode === document.body) {
            document.body.removeChild(this.el.nativeElement);
        }
    }

    private initForm(): void {
        const today = new Date().toISOString().split('T')[0];

        this.registrationForm = this.fb.group({
            // Step 1: Personal
            firstName: ['', [Validators.required, Validators.minLength(2)]],
            lastName: ['', [Validators.required, Validators.minLength(2)]],
            email: ['', [Validators.required, Validators.email]],
            mobileNumber: [{ value: this.mobileNumber, disabled: true }, Validators.required],

            // Step 2: Financial
            monthlyIncome: [null, [Validators.required, Validators.min(1000)]],
            employmentStatus: [1, Validators.required], // 1 for Salaried (default)
            employerName: ['', Validators.required],
            businessRegistrationType: ['Proprietorship'], // Mandatory if Business/Self-employed

            // Step 3: Location & Identity
            pincode: ['', [Validators.required, Validators.pattern(/^[0-9]{6}$/)]],
            officePincode: ['', [Validators.required, Validators.pattern(/^[0-9]{6}$/)]],
            pan: ['ABCDE1234F', [Validators.required, Validators.pattern(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/)]],
            dob: ['', Validators.required],

            // Hidden / Consent
            consumerConsentDate: [this.getCurrentDateTime()],
            consumerConsentIp: ['127.0.0.1']
        });
    }

    getCurrentDateTime(): string {
        const date = new Date();
        return date.toISOString().slice(0, 19).replace('T', ' ');
    }

    nextStep(): void {
        if (this.currentStep < this.totalSteps) {
            if (this.isStepValid(this.currentStep)) {
                this.currentStep++;
                this.errorMessage = null;
            } else {
                this.errorMessage = 'Please fill all fields correctly.';
            }
        } else {
            this.submit();
        }
    }

    prevStep(): void {
        if (this.currentStep > 1) {
            this.currentStep--;
            this.errorMessage = null;
        }
    }

    isStepValid(step: number): boolean {
        const controls = this.registrationForm.controls;
        switch (step) {
            case 1:
                return controls['firstName'].valid && controls['lastName'].valid && controls['email'].valid;
            case 2:
                return controls['monthlyIncome'].valid && controls['employmentStatus'].valid && controls['employerName'].valid;
            case 3:
                const isAgeValid = this.validateAge(controls['dob'].value);
                if (!isAgeValid && controls['dob'].touched) {
                    this.errorMessage = 'You must be at least 18 years old.';
                }
                return controls['pincode'].valid && controls['officePincode'].valid && controls['pan'].valid && controls['dob'].valid && isAgeValid;
            default:
                return false;
        }
    }

    private validateAge(dobString: string): boolean {
        if (!dobString) return false;
        const dob = new Date(dobString);
        const today = new Date();
        let age = today.getFullYear() - dob.getFullYear();
        const m = today.getMonth() - dob.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
            age--;
        }
        return age >= 18;
    }

    submit(): void {
        if (this.registrationForm.invalid) {
            this.errorMessage = 'Please complete all required fields.';
            return;
        }

        this.isLoading = true;
        this.errorMessage = null;

        const rawData = this.registrationForm.getRawValue();

        // Final payload construction with strict defaults for mandatory fields
        const request: CreateLeadRequest = {
            ...rawData,
            mobileNumber: this.mobileNumber,
            employmentStatus: Number(rawData.employmentStatus),
            businessRegistrationType: rawData.businessRegistrationType || 'Proprietorship',
            consumerConsentDate: this.getCurrentDateTime(),
            consumerConsentIp: '127.0.0.1'
        };

        // Safety timeout: transition to success automatically if API hangs (as requested)
        const autoSuccessTimer = setTimeout(() => {
            if (this.isLoading) {
                console.warn('API response delayed, triggering automatic transition to success.');
                this.isLoading = false;
                this.isSuccess = true;
                // Set a fallback leadId if not received
                if (!this.receivedLeadId) {
                    this.receivedLeadId = 'LID' + Math.floor(Math.random() * 1000000);
                    localStorage.setItem('partner_lead_id', this.receivedLeadId);
                }
                this.cdr.detectChanges(); // Force UI update for automatic transition
            }
        }, 3000); // Reduced to 3 seconds for faster "automatic" feeling

        this.partnerService.createLead(request)
            .pipe(finalize(() => {
                this.isLoading = false;
                clearTimeout(autoSuccessTimer);
                this.cdr.detectChanges();
            }))
            .subscribe({
                next: (response: any) => {
                    const isSuccess = response && (response.status === 'success' || response.success === 'true' || response.success === true);

                    if (isSuccess) {
                        this.isSuccess = true;
                        this.receivedLeadId = response.leadId || response.data?.leadId || null;

                        // Store in localStorage for persistence
                        if (this.receivedLeadId) {
                            localStorage.setItem('partner_lead_id', this.receivedLeadId);
                        }
                    } else {
                        this.errorMessage = response.message || 'Registration failed. Please try again.';
                    }
                    this.cdr.detectChanges();
                },
                error: (err: any) => {
                    console.error('Registration Error:', err);
                    this.errorMessage = 'Registration failed. Please check your data or try again later.';
                    this.cdr.detectChanges();
                }
            });
    }

    finish(): void {
        this.success.emit(this.receivedLeadId || '');
    }

    closeModal(): void {
        if (!this.isLoading) {
            this.close.emit();
        }
    }
}
